import React from 'react';
import { Box, Stack, TextField, MenuItem, IconButton, Typography, Alert, CircularProgress } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import EditorialButton from '../editorial/EditorialButton';
import { editorialInputSx } from '../editorial/editorialInputStyles';
import { computeSeatCost, validateSeatPeople, getSeatRateConfig, emptySeatPerson, computeMemberSeatSavings } from '../../service/seatPricing';
import { validateGuestContact, validateGuestSeatCount } from '../../service/guestSeatValidation';
import { formatCurrency, getEnrollmentSummary, getCheckoutSubmitLabel } from '../../service/membershipPricing';
import MembershipCheckout from './MembershipCheckout';
import CheckoutSummary from './CheckoutSummary';
import { RETURN_PATHS } from '../../service/checkoutReturn';

const cardSx = {
  p: { xs: 3, md: 4 },
  border: '1px solid rgba(36,34,88,0.15)',
  background: 'var(--paper)',
};

// One name + gender row, shared by both the included and additional seat
// groups so they read as visibly the same kind of thing, just in two
// clearly labeled places rather than two different UIs.
function SeatPersonRow({ person, onChange, onRemove, label }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
      {/* flex: 1 + minWidth: 0 is what actually makes this the field that
          grows/shrinks to fill the row. Without minWidth: 0, a flex item's
          default min-width is its content size, which fights flex-shrink
          and — with a 3rd sibling (the remove button) added — collapsed
          this field to nearly nothing while Gender and the icon kept
          their full size. `fullWidth` alone isn't enough once a row has
          more than two children. */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TextField
          label={label}
          value={person.name}
          onChange={(e) => onChange({ ...person, name: e.target.value })}
          sx={editorialInputSx}
          fullWidth
        />
      </Box>
      <TextField
        select
        label="Gender"
        value={person.gender}
        onChange={(e) => onChange({ ...person, gender: e.target.value })}
        sx={{ ...editorialInputSx, minWidth: { sm: 160 }, flexShrink: 0 }}
        fullWidth={false}
      >
        <MenuItem value="male">Male</MenuItem>
        <MenuItem value="female">Female</MenuItem>
      </TextField>
      {onRemove && (
        // width/minWidth explicitly overridden: this repo has a legacy
        // global `button { width: 100%; }` rule (src/styles/App.css, from
        // the old Stripe checkout page) that otherwise stretches every raw
        // <button> — including MUI's IconButton — to fill its flex
        // container, which is what was crushing the name field next to it
        // down to zero width.
        <IconButton
          onClick={onRemove}
          aria-label="Remove seat"
          sx={{ mt: { sm: 0.5 }, flexShrink: 0, width: 40, minWidth: 40 }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      )}
    </Stack>
  );
}

// High Holiday seat request — shared by BOTH members and guests, per the
// client's locked wireframe: this section always sits after tier
// selection and always shows something, whether or not a tier is picked.
//
// Two clearly separate groups, per the client's Aug 2026 revision:
//   - INCLUDED seats: exactly as many rows as the tier's allowance (2 for
//     most tiers, 1 for Singles, 0 for First-Year Couples and guests).
//     Never charged, but still need a name + gender each, since the
//     office needs to know who is actually in them.
//   - ADDITIONAL seats: an open add/remove list, every one of which is
//     charged at the member (or non-member, for guests) rate. This is
//     the ONLY thing that gets billed — see seatPricing.computeSeatCost.
//
// Guest mode (no tier selected): 0 included seats, so only the Additional
// section renders — every seat a guest adds is, definitionally, an
// additional seat. Non-member rate for every seat.
// forwardRef purely so the persistent tally bar at the bottom of the page
// can trigger the SAME guest submit as this form's own button, rather than
// duplicating the validation and error display that live in here.
const SeatForm = React.forwardRef(function SeatForm(
  {
    tier,
    seats,
    onChange,
    guestContact,
    onGuestContactChange,
    seatPricing,
    onContinue,
    onGuestRequestSubmitted,
    guestCheckoutState = {},
  },
  ref
) {
  const [errors, setErrors] = React.useState({ included: {}, additional: {} });

  const { enrolling: guestEnrolling, error: guestError, clientSecret: guestClientSecret } = guestCheckoutState;

  const isGuest = !tier;

  const rateConfig = getSeatRateConfig(tier, seatPricing);
  const includedSeatPeople = seats.includedSeatPeople || [];
  const additionalSeatPeople = seats.additionalSeatPeople || [];

  const cost = computeSeatCost({
    additionalSeats: additionalSeatPeople.length,
    includedSeats: rateConfig.includedSeats,
    isMemberRate: rateConfig.isMemberRate,
    extraSeatPrice: rateConfig.extraSeatPrice,
  });

  // Guest-only: the $360 non-member rate is set well above the $100
  // member rate specifically to make this gap worth showing — computed
  // off the additional-seat count so it updates live as a guest adds
  // seats, without them ever doing the subtraction themselves.
  const savings = isGuest ? computeMemberSeatSavings(additionalSeatPeople.length, seatPricing) : null;

  // Reuses the tally bar's own summary builder rather than assembling a
  // second set of line items, so the total shown above the card form can
  // never drift from the total shown on the seat form above it.
  const guestSummary = isGuest ? getEnrollmentSummary({ tier: null, seats, seatPricing }) : null;

  const updateIncluded = (index, person) => {
    const next = includedSeatPeople.map((p, i) => (i === index ? person : p));
    onChange({ ...seats, includedSeatPeople: next });
  };

  const updateAdditional = (index, person) => {
    const next = additionalSeatPeople.map((p, i) => (i === index ? person : p));
    onChange({ ...seats, additionalSeatPeople: next });
  };
  const addAdditional = () =>
    onChange({ ...seats, additionalSeatPeople: [...additionalSeatPeople, emptySeatPerson('male')] });
  const removeAdditional = (index) =>
    onChange({ ...seats, additionalSeatPeople: additionalSeatPeople.filter((_, i) => i !== index) });

  const handleContinue = () => {
    const { valid, errors: seatErrors } = validateSeatPeople({
      includedSeatPeople,
      additionalSeatPeople,
    });

    if (isGuest) {
      const contactCheck = validateGuestContact(guestContact);
      // Zero seats passes validateSeatPeople (it only checks rows that
      // exist), so without this a guest could submit nothing and get the
      // backend's raw 422 back. Checked first because "add a seat" is the
      // more useful thing to say when there is nothing to buy.
      const countCheck = validateGuestSeatCount(additionalSeatPeople);

      setErrors({
        ...seatErrors,
        contact: contactCheck.errors,
        // Shown only when there are no seat rows at all. If a row exists but
        // is unnamed, the per-row "enter a name" message already says it, and
        // two messages for one problem reads as noise. Gating below still
        // uses countCheck, so an unnamed-only list cannot be submitted.
        seatCount: additionalSeatPeople.length === 0 ? countCheck.error : null,
      });

      if (valid && contactCheck.valid && countCheck.valid) {
        onGuestRequestSubmitted?.({ seats, guestContact });
      }
      return;
    }

    setErrors(seatErrors);
    if (valid) onContinue();
  };

  // Exposed to the page so the tally bar's CONTINUE can call it. Deliberately
  // the whole handleContinue, not just the network call, so a click from the
  // bar runs the same validation and surfaces the same inline errors.
  React.useImperativeHandle(ref, () => ({ submitGuestRequest: handleContinue }));

  return (
    <Box maxWidth={840} mx="auto">
      <Box sx={isGuest ? { ...cardSx, border: 'none', background: 'transparent', px: 0 } : cardSx}>
        <Stack spacing={4}>
          {isGuest && guestClientSecret ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Seat request confirmed — complete payment below to reserve it.
              </Alert>
              <MembershipCheckout
                clientSecret={guestClientSecret}
                returnPath={RETURN_PATHS.guestSeats}
                submitLabel={getCheckoutSubmitLabel({ isGuest: true, amount: cost.extraSeatCost })}
                summary={
                  <CheckoutSummary
                    eyebrow="What you're paying for"
                    lines={[guestSummary?.membershipLine, guestSummary?.seatsLine]}
                    total={formatCurrency(cost.extraSeatCost)}
                    totalLabel="Total due today"
                    note={guestSummary?.dueTodayNote}
                  />
                }
              />
            </Box>
          ) : (
            <>
              {!isGuest && (
                <Stack spacing={2.5}>
                  <Box>
                    <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>
                      Included seats — no charge
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {rateConfig.includedSeats > 0
                        ? `Your membership includes ${rateConfig.includedSeats} High Holiday seat${rateConfig.includedSeats === 1 ? '' : 's'} at no charge. Tell us who's sitting in them.`
                        : "Your membership doesn't include any High Holiday seats — every seat below is an additional seat."}
                    </Typography>
                  </Box>

                  {includedSeatPeople.map((person, index) => (
                    <SeatPersonRow
                      key={`included-${index}`}
                      label={`Included seat ${index + 1} name`}
                      person={person}
                      onChange={(p) => updateIncluded(index, p)}
                    />
                  ))}
                  {errors.included && Object.keys(errors.included).length > 0 && (
                    <Typography color="error" variant="body2">
                      Enter a name for every included seat.
                    </Typography>
                  )}
                </Stack>
              )}

              <Stack spacing={2.5} sx={{ pt: isGuest ? 0 : 1, borderTop: isGuest ? 'none' : '1px solid rgba(36,34,88,0.1)' }}>
                <Box>
                  <Typography variant="overline" color="text.secondary" sx={{ display: 'block' }}>
                    Additional seats
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.5,
                      fontFamily: 'var(--font-body)',
                      fontSize: { xs: 17, md: 19 },
                      fontWeight: 700,
                      lineHeight: 1.5,
                      color: 'var(--navy-500)',
                    }}
                  >
                    {isGuest
                      ? `Guest seats are ${formatCurrency(rateConfig.extraSeatPrice)} each for non-members — no seats are included without a membership.`
                      : `Every seat you add here is ${formatCurrency(rateConfig.extraSeatPrice)} — for guests, family, anyone beyond your included seats above.`}
                  </Typography>

                  {isGuest && savings && savings.perSeatSavings > 0 && (
                    <Typography
                      sx={{
                        mt: 1,
                        fontFamily: 'var(--font-body)',
                        fontSize: 14,
                        color: 'var(--teal-500)',
                        fontWeight: 700,
                      }}
                    >
                      Members pay {formatCurrency(savings.memberPrice)} per seat instead —{' '}
                      {formatCurrency(savings.perSeatSavings)} less per seat.
                    </Typography>
                  )}
                </Box>

                {additionalSeatPeople.map((person, index) => (
                  <SeatPersonRow
                    key={`additional-${index}`}
                    label={`Additional seat ${index + 1} name`}
                    person={person}
                    onChange={(p) => updateAdditional(index, p)}
                    onRemove={() => removeAdditional(index)}
                  />
                ))}
                {errors.additional && Object.keys(errors.additional).length > 0 && (
                  <Typography color="error" variant="body2">
                    Enter a name for every additional seat.
                  </Typography>
                )}

                {errors.seatCount && (
                  <Typography color="error" variant="body2">
                    {errors.seatCount}
                  </Typography>
                )}

                <EditorialButton variant="ghost" onClick={addAdditional} sx={{ alignSelf: 'flex-start' }}>
                  + Add {additionalSeatPeople.length > 0 ? 'another' : 'a'} seat
                </EditorialButton>
              </Stack>

              {isGuest && (
                <Stack spacing={2.5}>
                  <Typography variant="overline" color="text.secondary">
                    Your contact info
                  </Typography>
                  <TextField
                    label="Name"
                    value={guestContact.name}
                    onChange={(e) => onGuestContactChange({ ...guestContact, name: e.target.value })}
                    error={Boolean(errors.contact?.name)}
                    helperText={errors.contact?.name || ' '}
                    sx={editorialInputSx}
                    fullWidth
                  />
                  <TextField
                    label="Email"
                    value={guestContact.email}
                    onChange={(e) => onGuestContactChange({ ...guestContact, email: e.target.value })}
                    error={Boolean(errors.contact?.email)}
                    helperText={errors.contact?.email || ' '}
                    sx={editorialInputSx}
                    fullWidth
                  />
                </Stack>
              )}

              <Box sx={{ pt: 1, borderTop: '1px solid rgba(36,34,88,0.1)' }}>
                <Typography variant="body2" color="text.secondary">
                  {rateConfig.includedSeats > 0 && !isGuest && `${rateConfig.includedSeats} included, no charge · `}
                  {cost.extraSeats} additional seat{cost.extraSeats === 1 ? '' : 's'}
                  {cost.extraSeats > 0 && ` at ${formatCurrency(cost.pricePerExtraSeat)} each`}
                </Typography>
                {cost.extraSeatCost > 0 && (
                  <Typography variant="body1" fontWeight={700} sx={{ mt: 0.5 }}>
                    {isGuest ? 'Guest seat total' : 'Additional seats total'}: {formatCurrency(cost.extraSeatCost)}
                  </Typography>
                )}
                {isGuest && savings && savings.totalSavings > 0 && (
                  <Typography sx={{ mt: 0.5, fontFamily: 'var(--font-body)', fontSize: 13.5, color: 'var(--teal-500)' }}>
                    You'd pay {formatCurrency(savings.totalSavings)} less as a member.
                  </Typography>
                )}
              </Box>

              <EditorialButton
                variant={isGuest ? 'ghost' : 'primary'}
                onClick={handleContinue}
                disabled={isGuest && guestEnrolling}
                fullWidth
              >
                {isGuest ? 'Request guest seats' : 'Continue'}
              </EditorialButton>

              {isGuest && guestEnrolling && (
                <Stack alignItems="center" py={1}>
                  <CircularProgress size={24} sx={{ color: 'var(--navy-500)' }} />
                </Stack>
              )}
              {isGuest && guestError && <Typography color="error">{guestError}</Typography>}
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
});

export default SeatForm;
